'use client'

import { useEffect, useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import type { GradeShareData } from '../lib/types'
import { Avatar } from './Avatar'
import { AppleIcon, AndroidIcon, CopyIcon, CloseIcon } from './icons'
import { APP_STORE_URL, GOOGLE_PLAY_URL, detectAppPlatform } from '../../appLinks'
import styles from './GetAppDialog.module.css'

type Platform = 'ios' | 'android'

function detectPlatform(): Platform {
  const platform = detectAppPlatform()
  return platform === 'android' ? 'android' : 'ios'
}

/**
 * "Get the app" dialog. Mobile renders a bottom sheet with the detected store;
 * desktop renders a centered modal with an iOS/Android switcher + QR code.
 * Layouts are CSS-toggled at 880px so the markup is SSR-safe.
 */
export function GetAppDialog({
  open,
  onClose,
  user,
  code,
}: {
  open: boolean
  onClose: () => void
  user: GradeShareData['user']
  code: string
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const [platform, setPlatform] = useState<Platform>('ios')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (open) setPlatform(detectPlatform())
  }, [open])

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
    // Lock the page behind the modal so it can't scroll under the backdrop
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      /* clipboard unavailable — no-op */
    }
  }

  const storeUrl = platform === 'ios' ? APP_STORE_URL : GOOGLE_PLAY_URL
  const StoreIcon = platform === 'ios' ? AppleIcon : AndroidIcon
  const storeLabel = platform === 'ios' ? 'Download for iOS' : 'Download for Android'

  const sharedBy = (
    <p className={styles.sharedBy}>
      Shared by
      <Avatar
        pokemonId={user.pokemonId}
        profileColor={user.profileColor}
        username={user.username}
        size={18}
      />
      <span className={styles.sharedByName}>{user.username}</span>
    </p>
  )

  const codeBox = (copyLabel: string) => (
    <div className={styles.codeRow}>
      {/* Dashed outline matches the app's scrubbing cursor line
          (ScrubCursorLine: strokeWidth 2.5, dasharray 6 6, round caps) */}
      <svg className={styles.codeDash} aria-hidden preserveAspectRatio="none">
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          rx="12"
          ry="12"
          fill="none"
          stroke="var(--text-faint)"
          strokeWidth="2.5"
          strokeDasharray="6 6"
          strokeLinecap="round"
        />
      </svg>
      <span className={styles.code}>{code}</span>
      <button className={styles.copyBtn} onClick={copy}>
        <CopyIcon size={16} />
        {copied ? 'Copied' : copyLabel}
      </button>
    </div>
  )

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose()
      }}
    >
      {/* ---------- Mobile bottom sheet ---------- */}
      <div className={styles.mobile}>
        <button className={styles.close} onClick={onClose} aria-label="Close">
          <CloseIcon size={24} />
        </button>
        <h2 className={styles.title}>Get a free AI grade</h2>
        {sharedBy}
        <p className={styles.label}>Copy your code, then enter it at sign-up</p>
        {codeBox('Copy code')}
        <a
          className={styles.downloadBtn}
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <StoreIcon size={18} />
          {storeLabel}
        </a>
      </div>

      {/* ---------- Desktop modal ---------- */}
      <div className={styles.desktop}>
        <button className={styles.close} onClick={onClose} aria-label="Close">
          <CloseIcon size={24} />
        </button>
        <h2 className={styles.title}>Get a free AI grade</h2>
        {sharedBy}

        <div className={styles.switcher} role="tablist" aria-label="Platform">
          <button
            role="tab"
            aria-selected={platform === 'ios'}
            className={`${styles.switch} ${platform === 'ios' ? styles.switchActive : ''}`}
            onClick={() => setPlatform('ios')}
          >
            <AppleIcon size={16} />
            iOS
          </button>
          <button
            role="tab"
            aria-selected={platform === 'android'}
            className={`${styles.switch} ${platform === 'android' ? styles.switchActive : ''}`}
            onClick={() => setPlatform('android')}
          >
            <AndroidIcon size={16} />
            Android
          </button>
        </div>

        <p className={styles.caption}>
          Scan to download on {platform === 'ios' ? 'iOS' : 'Android'}
        </p>
        <div className={styles.qrBox}>
          <QRCodeSVG value={storeUrl} size={156} bgColor="#ffffff" fgColor="#07001a" level="M" />
        </div>

        {codeBox('Copy')}
        <p className={styles.caption}>Copy your code, then enter it at sign-up</p>
      </div>
    </dialog>
  )
}
